import React from 'react';
import { useApi } from '@bumble/app/hooks/use-api';
import { apiError } from '@bumble/app/utils/api-error';
import BorderOuterOutlinedIcon from '@mui/icons-material/BorderOuterOutlined';
import ExpandLessOutlinedIcon from '@mui/icons-material/ExpandLessOutlined';
import ExpandMoreOutlinedIcon from '@mui/icons-material/ExpandMoreOutlined';
import {
  Box,
  Button,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'react-toastify';

const toggleStyles = { textTransform: 'none', fontWeight: 700 };
interface EndpointResizeProps {
  onSubmit: () => void;
  endpointNode: string;
}
export const EndpointResize = ({ onSubmit, endpointNode }: EndpointResizeProps) => {
  const api = useApi();
  const [size, setSize] = React.useState('up');
  const [disableSubmit, setDisableSubmit] = React.useState(false);

  const handleChange = (e: React.MouseEvent<HTMLElement>, newSize: string) => {
    setSize(newSize);
  };

  const epResize = useMutation(api.resizeEndpointThroughputApiV1ResizeEndpointThroughputPost, {
    onSuccess() {
      setDisableSubmit(false);
      toast.success('Resized Endpoint successfully.');
      onSubmit();
    },
    onError: (error) => {
      apiError(error);
      setDisableSubmit(false);
    },
  });

  const handleSubmit = () => {
    setDisableSubmit(true);
    epResize.mutate({
      ep_id: endpointNode,
      direction: size,
    });
  };

  return (
    <Box pt={2}>
      <Typography variant="body1" sx={{ mb: 6 }}>
        Enhance the performance of your AWS-deployed Endpoints with dynamic resizing. This
        functionality allows for real-time adjustments, ensuring your endpoints always operate at
        peak efficiency.
      </Typography>
      <Box display="flex" justifyContent="center" sx={{ mb: 4 }}>
        <ToggleButtonGroup
          aria-label="Resize Endpoints"
          value={size}
          onChange={handleChange}
          exclusive
          color="primary"
          size="small"
        >
          <ToggleButton value="up" sx={toggleStyles}>
            <ExpandLessOutlinedIcon />
            Size Up
          </ToggleButton>
          <ToggleButton value="" disabled>
            <BorderOuterOutlinedIcon />
          </ToggleButton>
          <ToggleButton value="down" sx={toggleStyles}>
            Size Down
            <ExpandMoreOutlinedIcon />
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>
      <Box display="flex" justifyContent="flex-end" sx={{ mt: 5 }}>
        <Button variant="outlined" onClick={handleSubmit} disabled={disableSubmit}>
          {disableSubmit ? (
            <>
              <CircularProgress size={16} sx={{ mr: 1 }} color="inherit" />
              Working...
            </>
          ) : (
            'Confirm'
          )}
        </Button>
      </Box>
    </Box>
  );
};
